import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import Select from 'react-select';
import * as Datetime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';
import { TabContent, TabPane, Nav, NavItem, NavLink } from 'reactstrap';
import classnames from 'classnames';
import CountUp from 'react-countup';
import Grid from '../layouts/gridComponent';
import { DEFAULT_CONFIG } from './interplantMovementTrackingConfig';
import { fetchServiceConfig } from '../common/fetchServiceConfig';
import './interplantmovementtracking.css';

var redirectURL = require('../redirectURL');
var moment = require('moment');

const ALL_OPTION = { value: 'all', label: 'All' };
const CONFIG_TYPE = 'app';
const SERVICE_NAME = 'interplant_movement_tracking_dashboard';
const CONFIG_SOURCE = 'ui';
const DISPLAY_DATE_FORMAT = 'DD-MM-YYYY';

const IconTruck = () => (
	<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<rect x="1" y="6" width="14" height="10"></rect>
		<path d="M15 10h4l3 3v3h-7z"></path>
		<circle cx="6" cy="18.5" r="1.5"></circle>
		<circle cx="18" cy="18.5" r="1.5"></circle>
	</svg>
);

const IconFile = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
		<polyline points="14 2 14 8 20 8"></polyline>
	</svg>
);

const IconSearch = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<circle cx="11" cy="11" r="8"></circle>
		<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
	</svg>
);

const IconRefresh = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<polyline points="23 4 23 10 17 10"></polyline>
		<polyline points="1 20 1 14 7 14"></polyline>
		<path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
	</svg>
);

const IconCalendar = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<rect x="3" y="4" width="18" height="18" rx="2"></rect>
		<line x1="16" y1="2" x2="16" y2="6"></line>
		<line x1="8" y1="2" x2="8" y2="6"></line>
		<line x1="3" y1="10" x2="21" y2="10"></line>
	</svg>
);

const IconMapPin = () => (
	<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
		<circle cx="12" cy="10" r="3"></circle>
	</svg>
);

const IconBarChart = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<line x1="12" y1="20" x2="12" y2="10"></line>
		<line x1="18" y1="20" x2="18" y2="4"></line>
		<line x1="6" y1="20" x2="6" y2="16"></line>
	</svg>
);

const IconClose = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<line x1="18" y1="6" x2="6" y2="18"></line>
		<line x1="6" y1="6" x2="18" y2="18"></line>
	</svg>
);

const KPI_ICONS = { truck: IconTruck, pin: IconMapPin };

// Maps the raw numeric `status` code to its display label via the config-driven
// `statusLabels` lookup (interplantMovementTrackingConfig.js / the fetched service config) —
// the function itself still has to live here rather than in the config file because
// fetchServiceConfig only carries plain JSON, not functions.
function getStatusLabel(params, statusLabels) {
	var status = params.data && params.data.status;
	var labels = statusLabels || DEFAULT_CONFIG.dashboard.statusLabels;
	return (labels && labels[status]) || "";
}

const DEFAULT_DATE_FORMAT = "DD-MM-YYYY HH:mm:ss";

// Local, screen-scoped replacement for the shared dateComponent.js "dateFormater" cellRenderer,
// used (via valueGetter, not cellRenderer/valueFormatter — see mapColumnDef below) for every
// column whose config marks cellRenderer:"dateFormater". A plain framework-agnostic function (no
// React component/frameworkComponents registry involved) — the shared gridComponent.js only
// applies a frameworkComponents prop from componentDidMount/setState, which runs after AG Grid
// (this app pins the v22-era @ag-grid-community/community+enterprise all-modules) has already
// been constructed, so anything registered that way is silently ignored; a raw React component
// passed directly as colDef.cellRenderer doesn't work either (AG Grid's core cellRenderer
// contract expects a string/DOM node back, not a React element, so it renders blank). Guards
// null/not-yet-populated/invalid values (returns "" instead of "Invalid date") — e.g.
// "Left / Unloaded On" is genuinely empty for a truck that hasn't left yet.
function formatIpmtDate(value, dateFormat) {
	if (!value || !moment(value).isValid()) { return ""; }
	return moment(value).format(dateFormat || DEFAULT_DATE_FORMAT);
}

// mapColumnDef() uses formatIpmtDate() as a valueGetter (not a cellRenderer/valueFormatter) so
// the column's *value* itself is the formatted display string. That way AG Grid's normal default
// text filter (same as every other column here — no special date filter type) filters/matches
// against that same formatted text: what's shown is exactly what's searched, with no separate
// raw-vs-display split. The one thing that breaks as a result is sorting — a formatted string
// like "15-01-2026 10:30:00" sorts lexicographically, not chronologically — so this comparator is
// wired as the column's own `comparator` to sort by the row's real underlying date value instead
// of the formatted string.
function ipmtDateSortComparator(field, nodeA, nodeB) {
	var rawA = nodeA && nodeA.data ? nodeA.data[field] : null;
	var rawB = nodeB && nodeB.data ? nodeB.data[field] : null;
	var timeA = rawA ? new Date(rawA).getTime() : 0;
	var timeB = rawB ? new Date(rawB).getTime() : 0;
	return timeA - timeB;
}

export default class InterplantMovementTrackingDashboard extends Component {

	constructor(props) {
		super(props);
		this.state = {
			config: DEFAULT_CONFIG.dashboard,
			activeTabId: DEFAULT_CONFIG.dashboard.defaultTab,
			consignerOptions: [ALL_OPTION],
			selectedConsigners: [ALL_OPTION],
			startDate: '',
			endDate: '',
			rowData: [],
			statusSummaryRows: [],
			loadshow: 'show-n',
			overly: 'show-n',
			errorMessage: '',
			sliderTranslate: '',
			sliderTitle: '',
			sliderColumns: [],
			sliderRowData: [],
			sliderLoading: false
		};
	}

	componentDidMount() {
		fetchServiceConfig({ config_type: CONFIG_TYPE, service_name: SERVICE_NAME, source: CONFIG_SOURCE })
			.then((response) => {
				var payload = response && response.data;
				var fetchedDashboard = payload && payload.found && payload.config && payload.config.dashboard;
				var dashboardConfig = fetchedDashboard || DEFAULT_CONFIG.dashboard;
				var defaultTab = dashboardConfig.defaultTab || this.state.activeTabId;
				this.setState({ config: dashboardConfig, activeTabId: defaultTab }, () => {
					this.loadTab(defaultTab);
				});
			})
			.catch(() => {
				// Configuration API unavailable — continue with the local default configuration.
				this.loadTab(this.state.activeTabId);
			});
	}

	getTabDef(tabId) {
		var tabs = this.state.config.tabs || [];
		return tabs.find((t) => t.id === tabId) || tabs[0];
	}

	computeDefaultDateRange() {
		var cfg = (this.state.config && this.state.config.defaultDateRange) || {};
		var mode = cfg.mode || 'none';
		var today = moment();

		if (mode === 'today') {
			return { startDate: today.format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
		}
		if (mode === 'lastNDays') {
			var days = Number(cfg.days) > 0 ? Number(cfg.days) : 1;
			return {
				startDate: moment().subtract(days - 1, 'days').format('YYYY-MM-DD'),
				endDate: today.format('YYYY-MM-DD')
			};
		}
		if (mode === 'monthToDate') {
			return {
				startDate: moment().startOf('month').format('YYYY-MM-DD'),
				endDate: today.format('YYYY-MM-DD')
			};
		}
		if (mode === 'lastNMonths') {
			// Calendar-month subtraction (moment handles variable month lengths correctly:
			// 1 month back from 1-Sep gives 1-Aug, from 1-Mar gives 1-Feb, etc.) — not a fixed
			// day count, so it stays accurate across 28/29/30/31-day months.
			var months = Number(cfg.months) > 0 ? Number(cfg.months) : 1;
			return {
				startDate: moment().subtract(months, 'months').format('YYYY-MM-DD'),
				endDate: today.format('YYYY-MM-DD')
			};
		}
		// mode === 'none' (or unrecognized) — no default date filter, matches prior behavior.
		return { startDate: '', endDate: '' };
	}

	loadTab(tabId) {
		var tab = this.getTabDef(tabId);
		if (!tab) { return; }
		var defaultRange = this.computeDefaultDateRange();
		this.setState({
			selectedConsigners: [ALL_OPTION],
			startDate: defaultRange.startDate,
			endDate: defaultRange.endDate
		}, () => {
			var filters = { startDate: this.state.startDate, endDate: this.state.endDate };
			this.fetchConsigners(tab.whp_trip_type_identifier).then(() => {
				this.fetchStatusSummary(filters);
			});
			this.fetchData(tab.whp_trip_type_identifier, filters);
		});
	}

	onToggleTab(tabId) {
		if (this.state.activeTabId === tabId) { return; }
		this.setState({ activeTabId: tabId }, () => {
			this.loadTab(tabId);
		});
	}

	fetchConsigners(whp_trip_type_identifier) {
		return redirectURL.post('/consignments/interplantmovementtrackingconsigners', { whp_trip_type_identifier: whp_trip_type_identifier })
			.then((response) => {
				var consigner = (this.state.config.filters && this.state.config.filters.consigner) || {};
				var valueField = consigner.valueField || 'plant_code';
				var labelField = consigner.labelField || 'plant_name';
				var plants = (response && response.data) || [];
				var options = plants.map((p) => ({
					value: p[valueField],
					label: p[labelField] + ' (' + p[valueField] + ')'
				}));
				this.setState({ consignerOptions: [ALL_OPTION, ...options] });
			})
			.catch(() => {
				this.setState({ consignerOptions: [ALL_OPTION] });
			});
	}

	getSelectedConsignerCodes() {
		var selected = this.state.selectedConsigners || [];
		var hasAll = selected.some((o) => o.value === 'all');
		if (hasAll || selected.length === 0) { return []; }
		return selected.map((o) => o.value);
	}

	fetchData(whp_trip_type_identifier, filters) {
		this.setState({ loadshow: 'show-m', overly: 'show-m', errorMessage: '' });
		var params = { whp_trip_type_identifier: whp_trip_type_identifier };
		if (Array.isArray(filters.plant_code) && filters.plant_code.length) {
			params.plant_code = filters.plant_code;
		}
		if (filters.startDate && filters.endDate) {
			params.startDate = filters.startDate;
			params.endDate = filters.endDate;
		}
		redirectURL.post('/consignments/interplantmovementtracking', params)
			.then((response) => {
				this.setState({ rowData: (response && response.data) || [], loadshow: 'show-n', overly: 'show-n' });
			})
			.catch(() => {
				this.setState({
					rowData: [],
					loadshow: 'show-n',
					overly: 'show-n',
					errorMessage: (this.state.config.table && this.state.config.table.errorLabel) || 'Unable to load data. Please try again.'
				});
			});
	}

	// Consigner codes to scope a `consignments` query by: the user's explicit selection when
	// one exists, otherwise every plant known for the active tab (from `consignerOptions`,
	// itself fetched per whp_trip_type_identifier — see fetchConsigners). Sending the full
	// per-tab plant list on "All" (instead of omitting the filter) keeps the KPI cards/status
	// summary/detail drawer scoped to recognized plants only.
	getEffectiveConsignerCodes() {
		var selected = this.getSelectedConsignerCodes();
		if (selected.length) { return selected; }
		return this.state.consignerOptions.filter((o) => o.value !== 'all').map((o) => o.value);
	}

	fetchStatusSummary(filters) {
		var statusSummary = this.state.config.statusSummary || {};
		var tab = this.getTabDef(this.state.activeTabId);
		var params = {
			whp_trip_type_identifier: tab.whp_trip_type_identifier,
			// The breakdown grouping field is config-driven end-to-end: whatever this config
			// says the breakdown groups by is exactly what the backend aggregation groups by
			// (see buildInterplantMovementStatusBaseQuery / getInterplantMovementStatusSummary
			// in hero_node) — not hardcoded to consignee_code/consignee_name on either side.
			groupByValueField: statusSummary.breakdownValueField || 'consignee_code',
			groupByLabelField: statusSummary.breakdownLabelField || 'consignee_name'
		};
		if (Array.isArray(statusSummary.allowedStatuses) && statusSummary.allowedStatuses.length) {
			// Config-driven status whitelist for what gets fetched from `consignments` here —
			// the backend falls back to the same [1,2,3,4,5] default if this is omitted (see
			// DEFAULT_INTERPLANT_ALLOWED_STATUSES in hero_node's consignmentController.js).
			params.allowedStatuses = statusSummary.allowedStatuses;
		}
		var consignerCodes = this.getEffectiveConsignerCodes();
		if (consignerCodes.length) {
			params.consigner_code = consignerCodes;
		}
		if (filters.startDate && filters.endDate) {
			params.startDate = filters.startDate;
			params.endDate = filters.endDate;
		}
		redirectURL.post('/consignments/interplantmovementstatussummary', params)
			.then((response) => {
				this.setState({ statusSummaryRows: (response && response.data) || [] });
			})
			.catch(() => {
				this.setState({ statusSummaryRows: [] });
			});
	}

	buildGroupTotals() {
		var statusSummary = this.state.config.statusSummary || {};
		var groups = statusSummary.groups || [];
		var breakdownValueField = statusSummary.breakdownValueField || 'consignee_code';
		var breakdownLabelField = statusSummary.breakdownLabelField || 'consignee_name';
		var rows = this.state.statusSummaryRows || [];

		return groups.map((group) => {
			var statusValues = group.statusValues || [];
			var matched = rows.filter((r) => statusValues.indexOf(r.status) >= 0);
			var total = matched.reduce((sum, r) => sum + (r.count || 0), 0);

			var breakdownMap = {};
			matched.forEach((r) => {
				var key = r[breakdownValueField];
				if (!breakdownMap[key]) {
					breakdownMap[key] = { value: key, label: r[breakdownLabelField] || key, count: 0 };
				}
				breakdownMap[key].count += (r.count || 0);
			});

			var breakdown = Object.keys(breakdownMap)
				.map((k) => breakdownMap[k])
				.sort((a, b) => (a.label || '').localeCompare(b.label || ''));

			return { group: group, total: total, breakdown: breakdown };
		});
	}

	getTotalRecordsCount() {
		// Total Records reflects the consignments-collection data (statusSummaryRows) — the same
		// source the KPI cards use — not the sndtransittimes-backed rowData/grid below.
		var rows = this.state.statusSummaryRows || [];
		return rows.reduce((sum, r) => sum + (r.count || 0), 0);
	}

	onChangeConsigner = (selected) => {
		// Empty selection is a valid state (not forced back to [ALL_OPTION]) — it already
		// resolves to "no consigner filter" exactly like ALL_OPTION does, via
		// getSelectedConsignerCodes() below. Forcing it back to ALL_OPTION previously made it
		// impossible to end up with only specific plants selected after removing "All".
		this.setState({ selectedConsigners: selected || [] });
	}

	handlerStartDateTime = (date) => {
		this.setState({ startDate: (date && date.format) ? date.format('YYYY-MM-DD') : '' });
	}

	handlerEndDateTime = (date) => {
		this.setState({ endDate: (date && date.format) ? date.format('YYYY-MM-DD') : '' });
	}

	onSubmitFilters = () => {
		if (this.state.startDate && this.state.endDate && moment(this.state.startDate).isAfter(moment(this.state.endDate))) {
			this.setState({ errorMessage: 'From Date must not be after To Date.' });
			return;
		}
		var tab = this.getTabDef(this.state.activeTabId);
		var filters = {
			plant_code: this.getSelectedConsignerCodes(),
			startDate: this.state.startDate,
			endDate: this.state.endDate
		};
		this.fetchData(tab.whp_trip_type_identifier, filters);
		this.fetchStatusSummary(filters);
	}

	onResetFilters = () => {
		var defaultRange = this.computeDefaultDateRange();
		this.setState({
			selectedConsigners: [ALL_OPTION],
			startDate: defaultRange.startDate,
			endDate: defaultRange.endDate
		}, () => {
			var tab = this.getTabDef(this.state.activeTabId);
			var filters = { startDate: this.state.startDate, endDate: this.state.endDate };
			this.fetchData(tab.whp_trip_type_identifier, filters);
			this.fetchStatusSummary(filters);
		});
	}

	// Shared by buildColumnDefs() and openStatusDetailSlider() for every non-"status" column.
	// minWidth (not width) + flex lets columns grow to fill the grid's available width instead
	// of always leaving/needing horizontal scroll — the configured width becomes a floor, not a
	// fixed size. cellRenderer:"dateFormater" is upgraded to a valueFormatter (see formatIpmtDate
	// above for why this uses valueFormatter rather than a custom cellRenderer) plus
	// agDateColumnFilter with a comparator, so date columns get both a formatted display AND a
	// proper date-range filter UI instead of raw-ISO-string text matching. Every other
	// cellRenderer value (or none) passes through unchanged. Purely presentational: the columns
	// themselves, their fields and business meaning are untouched.
	//
	// Date format resolution is two-stage/config-driven: a column's own `dateFormat` (set right
	// next to its `cellRenderer` in interplantMovementTrackingConfig.js / the DB config) wins when
	// present — that's the per-column override — else the screen-wide `dashboard.dateFormat`
	// applies as the global default, else DEFAULT_DATE_FORMAT (the format the shared
	// dateComponent.js renderer has always used) as the last resort so an older config document
	// with neither field keeps behaving exactly as before.
	mapColumnDef = (c) => {
		if (c.cellRenderer === 'dateFormater') {
			var dateFormat = c.dateFormat || this.state.config.dateFormat || DEFAULT_DATE_FORMAT;
			var field = c.field;
			return {
				headerName: c.headerName,
				field: field,
				minWidth: c.width,
				flex: 1,
				hide: c.hide,
				valueGetter: (params) => formatIpmtDate(params.data && params.data[field], dateFormat),
				comparator: (valueA, valueB, nodeA, nodeB) => ipmtDateSortComparator(field, nodeA, nodeB)
			};
		}
		return {
			headerName: c.headerName,
			field: c.field,
			minWidth: c.width,
			flex: 1,
			hide: c.hide,
			cellRenderer: c.cellRenderer
		};
	}

	buildColumnDefs() {
		var columns = (this.state.config.table && this.state.config.table.columns) || [];
		return columns.map(this.mapColumnDef);
	}

	openStatusDetailSlider = (group, detailConsigneeCode, detailLabel) => {
		var statusSummary = this.state.config.statusSummary || {};
		var baseColumns = statusSummary.baseDetailColumns || [];
		var columnDefs = [...baseColumns, ...(group.detailColumns || [])].map((c) => {
			if (c.field === 'status') {
				return {
					headerName: c.headerName,
					field: c.field,
					width: c.width,
					hide: c.hide,
					pinned: 'left',
					filter: 'agSetColumnFilter',
					resizable: true,
					valueGetter: (params) => getStatusLabel(params, this.state.config.statusLabels)
				};
			}
			return this.mapColumnDef(c);
		});

		var tab = this.getTabDef(this.state.activeTabId);
		var params = {
			whp_trip_type_identifier: tab.whp_trip_type_identifier,
			status: group.statusValues || []
		};
		if (Array.isArray(statusSummary.rangeRules) && statusSummary.rangeRules.length) {
			// Config-driven Range/No Of Days computation (applyInterplantRangeRules in hero_node's
			// consignmentController.js) — sent as-is the same way allowedStatuses/groupByValueField
			// already are for this screen, so hero_node applies no hardcoded business thresholds.
			params.rangeRules = statusSummary.rangeRules;
		}
		var consignerCodes = this.getEffectiveConsignerCodes();
		if (consignerCodes.length) {
			params.consigner_code = consignerCodes;
		}
		if (this.state.startDate && this.state.endDate) {
			params.startDate = this.state.startDate;
			params.endDate = this.state.endDate;
		}
		if (detailConsigneeCode) {
			params.detailConsigneeCode = detailConsigneeCode;
		}

		this.setState({
			sliderTranslate: 'slider-translate-75p',
			sliderTitle: detailLabel ? (group.label + ' — ' + detailLabel) : group.label,
			sliderColumns: columnDefs,
			sliderRowData: [],
			sliderLoading: true
		});

		redirectURL.post('/consignments/interplantmovementstatusdetails', params)
			.then((response) => {
				this.setState({ sliderRowData: (response && response.data) || [], sliderLoading: false });
			})
			.catch(() => {
				this.setState({ sliderRowData: [], sliderLoading: false });
			});
	}

	onCloseSlider = () => {
		this.setState({ sliderTranslate: '' });
	}

	renderDetailDrawer() {
		// Rendered through a portal straight onto document.body so the drawer's fixed
		// positioning is always relative to the real viewport (60/75/100vw, 100vh) —
		// unaffected by any ancestor page-layout wrapper. All existing slider state/data
		// flow (sliderTranslate/sliderTitle/sliderColumns/sliderRowData/sliderLoading,
		// openStatusDetailSlider, onCloseSlider) is unchanged; only where this markup is
		// mounted and how it's styled is different from the previous slide-r/overlay-part.
		var isOpen = !!this.state.sliderTranslate;
		var drawerConfig = this.state.config.drawer || {};
		var drawerStyle = {
			'--ipmt-drawer-width': (drawerConfig.widthPercent || 60) + 'vw',
			'--ipmt-drawer-tablet-width': (drawerConfig.tabletWidthPercent || 75) + 'vw',
			'--ipmt-drawer-mobile-width': (drawerConfig.mobileWidthPercent || 100) + 'vw'
		};
		var drawer = (
			<React.Fragment>
				<div
					className={'ipmt-drawer-backdrop' + (isOpen ? ' ipmt-drawer-backdrop-open' : '')}
					onClick={this.onCloseSlider}>
				</div>
				<div className={'ipmt-drawer' + (isOpen ? ' ipmt-drawer-open' : '')} style={drawerStyle}>
					<div className="ipmt-drawer-header">
						<h4 className="ipmt-drawer-title">{this.state.sliderTitle}</h4>
						<span className="ipmt-drawer-close" onClick={this.onCloseSlider}><IconClose /></span>
					</div>
					<div className="ipmt-drawer-body">
						{this.state.sliderLoading ? (
							<div className="ipmt-drawer-loading">
								<span className="ipmt-spinner"></span>
								<div>Loading details...</div>
							</div>
						) : null}
						<Grid
							columnaxis={this.state.sliderColumns}
							columnyaxis={this.state.sliderRowData}
						/>
					</div>
				</div>
			</React.Fragment>
		);
		return ReactDOM.createPortal(drawer, document.body);
	}

	renderDateField(label, value, onChange, name) {
		return (
			<div className="ipmt-field">
				<label>{label}</label>
				<div className="ipmt-date-wrap">
					<Datetime
						value={value ? moment(value) : ''}
						disableCloseOnClickOutside={false}
						closeOnSelect={true}
						inputProps={{ placeholder: label, name: name, autoComplete: 'off', readOnly: true }}
						dateFormat={DISPLAY_DATE_FORMAT}
						timeFormat={false}
						onChange={onChange}
					/>
					<span className="ipmt-date-icon"><IconCalendar /></span>
				</div>
			</div>
		);
	}

	renderStatusSummary() {
		var statusSummary = this.state.config.statusSummary || {};
		if (!statusSummary.enabled) { return null; }

		var groupTotals = this.buildGroupTotals();

		return (
			<div className="ipmt-kpi-card-wrap">
				{groupTotals.map((entry) => {
					var GroupIcon = KPI_ICONS[entry.group.icon] || IconTruck;
					return (
						<div className="ipmt-kpi-col" key={entry.group.id}>
							<div className="ipmt-kpi-col-header">
								<span className={'ipmt-kpi-icon ' + (entry.group.colorClass || '')}><GroupIcon /></span>
								<span className="ipmt-kpi-label">{entry.group.label}</span>
							</div>
							<div
								className={'ipmt-kpi-total ' + (entry.group.colorClass || '')}
								onClick={() => this.openStatusDetailSlider(entry.group)}>
								<CountUp end={entry.total} duration={0.6} />
							</div>
							<div className="ipmt-kpi-breakdown">
								{entry.breakdown.map((b, idx) => (
									<div
										key={b.value}
										className={'ipmt-kpi-breakdown-row' + (idx % 2 === 1 ? ' ipmt-kpi-breakdown-row-alt' : '')}
										onClick={() => this.openStatusDetailSlider(entry.group, b.value, b.label)}>
										<span className="ipmt-kpi-breakdown-label">{b.label}</span>
										<span className="ipmt-kpi-breakdown-right">
											<span className="ipmt-kpi-breakdown-view">View</span>
											<span className="ipmt-kpi-breakdown-count">{b.count}</span>
										</span>
									</div>
								))}
							</div>
						</div>
					);
				})}
			</div>
		);
	}

	render() {
		var config = this.state.config;
		var filters = config.filters || {};
		var table = config.table || {};
		var tabs = config.tabs || [];

		return (
			<div className="container-fluid ipmt-page">
				<div className="ipmt-header-row">
					<div className="ipmt-header-left">
						<span className="ipmt-header-icon"><IconTruck /></span>
						<div>
							<h4 className="ipmt-title">{config.title}</h4>
							{config.subtitle ? <div className="ipmt-subtitle">{config.subtitle}</div> : null}
						</div>
					</div>
					{config.totalRecordsLabel ? (
						<div className="ipmt-total-records">
							<span className="ipmt-total-records-icon"><IconBarChart /></span>
							<div>
								<div className="ipmt-total-records-label">{config.totalRecordsLabel}</div>
								<div className="ipmt-total-records-value">{this.getTotalRecordsCount()}</div>
							</div>
						</div>
					) : null}
				</div>

				<Nav tabs className="ipmt-tabs">
					{tabs.map((tab) => (
						<NavItem key={tab.id}>
							<NavLink
								className={classnames('ipmt-tab-link', { active: this.state.activeTabId === tab.id })}
								onClick={() => { this.onToggleTab(tab.id); }}>
								<span className="ipmt-tab-icon"><IconFile /></span>{tab.label}
							</NavLink>
						</NavItem>
					))}
				</Nav>

				<TabContent activeTab={this.state.activeTabId}>
					{tabs.map((tab) => (
						<TabPane tabId={tab.id} key={tab.id}>
							<div className="ipmt-filter-card">
								<div className="ipmt-filters">
									{filters.consigner && filters.consigner.enabled ? (
										<div className="ipmt-field ipmt-field-consigner">
											<label>{filters.consigner.label}</label>
											<Select
												classNamePrefix="ipmt-select"
												className="ipmt-select"
												placeholder={"All Consigners"}
												isMulti={true}
												closeMenuOnSelect={false}
												value={this.state.selectedConsigners}
												onChange={this.onChangeConsigner}
												options={this.state.consignerOptions}
												styles={{
													valueContainer: (base) => ({ ...base, flexWrap: 'wrap' }),
													control: (base) => ({ ...base, flexWrap: 'wrap' })
												}}
											/>
										</div>
									) : null}
									{filters.fromDate && filters.fromDate.enabled
										? this.renderDateField(filters.fromDate.label, this.state.startDate, this.handlerStartDateTime, 'startDate')
										: null}
									{filters.toDate && filters.toDate.enabled
										? this.renderDateField(filters.toDate.label, this.state.endDate, this.handlerEndDateTime, 'endDate')
										: null}
									<div className="ipmt-filter-actions">
										<button type="button" className="ipmt-btn ipmt-btn-submit" onClick={this.onSubmitFilters}>
											<IconSearch /> Submit
										</button>
										<button type="button" className="ipmt-btn ipmt-btn-reset" onClick={this.onResetFilters}>
											<IconRefresh /> Reset
										</button>
									</div>
								</div>
							</div>

							{this.renderStatusSummary()}

							{table.enabled !== false ? (
								<React.Fragment>
									{this.state.errorMessage ? (
										<div className="ipmt-message ipmt-message-error">{this.state.errorMessage}</div>
									) : null}

									{(!this.state.errorMessage && this.state.loadshow === 'show-n' && this.state.rowData.length === 0) ? (
										<div className="ipmt-message ipmt-message-info">{table.emptyStateLabel}</div>
									) : null}

									<div className="ipmt-grid-wrap">
										<Grid
											columnaxis={this.buildColumnDefs()}
											columnyaxis={this.state.rowData}
										/>
									</div>
								</React.Fragment>
							) : null}
						</TabPane>
					))}
				</TabContent>

				{this.renderDetailDrawer()}

				<div className={"dataLoadpage " + this.state.loadshow}></div>
				<div className={"dataLoadpageimg " + this.state.loadshow}>
					<div className="loader-box"><div className="loader-box"><div className="rotate dashed colored"></div></div></div>
					<div style={{ textAlign: 'center' }}>{table.loadingLabel}</div>
				</div>
			</div>
		);
	}
}
